import React from 'react';
import { ChevronLeft } from 'lucide-react';
import type { NavTrailCrumb, NavTrailTarget } from '@turn-connect/shared';
import './placeHeader.css';

export interface BreadcrumbsProps {
  crumbs: NavTrailCrumb[];
  /** In-app navigation for a plain click on a crumb. */
  onNavigate: (target: NavTrailTarget) => void;
  /** The crumb's address, so it is a real link (new tab, copy link). */
  hrefFor: (target: NavTrailTarget) => string | undefined;
  /**
   * Touch widths: only the way up — "‹ Parent" — as one 44px link, the way
   * the phone app shows it. The page's own name is the title below.
   */
  compact?: boolean;
}

const plainClick = (e: React.MouseEvent) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0);

/**
 * "All accounts › Splits Demo Motors › Express Lane › Settings": every level
 * the person may open is a link; the last is the page itself. Hidden when the
 * page is the only level (nothing above it to go to).
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ crumbs, onNavigate, hrefFor, compact = false }) => {
  if (crumbs.length < 2) return null;
  const link = (c: NavTrailCrumb, content: React.ReactNode) => (
    <a
      className="ph-crumb-link"
      href={hrefFor(c.target) ?? '#'}
      data-testid={`crumb-${c.level}`}
      onClick={(e) => {
        if (!plainClick(e)) return;
        e.preventDefault();
        onNavigate(c.target);
      }}
    >
      {content}
    </a>
  );
  if (compact) {
    const parent = crumbs[crumbs.length - 2];
    return (
      <nav aria-label="Breadcrumb" className="ph-crumbs ph-crumbs--compact">
        <ol>
          <li>
            {link(
              parent,
              <>
                <ChevronLeft size={18} aria-hidden="true" />
                {parent.standaloneLabel ?? parent.label}
              </>
            )}
          </li>
        </ol>
      </nav>
    );
  }
  return (
    <nav aria-label="Breadcrumb" className="ph-crumbs">
      <ol>
        {crumbs.map((c, i) => (
          <li key={c.level}>
            {i > 0 && (
              <span aria-hidden="true" className="ph-crumb-sep">
                ›
              </span>
            )}
            {c.current ? (
              <span aria-current="page" className="ph-crumb-current" data-testid={`crumb-${c.level}`}>
                {c.label}
              </span>
            ) : (
              link(c, c.label)
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
